import { useState } from 'react';
import { CalendarCheck, CalendarClock, CalendarPlus, DoorOpen, Lock, MapPin, Video } from 'lucide-react';
import {
  Button,
  Popover,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@clarix/ui';
import { Badge, PopoverContentWithArrow, type BadgeProps } from '@clarix/ui-shared';
import { cn } from '@clarix/ui/lib/cn';
import { Faces, PulseDot } from './chipParts';
import { chipView, type ChipKind, type ChipView } from './chipView';
import { CALENDAR_PATH, CHIP, QM_POLL } from './constants';
import { CHIP_COPY } from './copy';
import { eventLabel, formatDayRange, isLive, type EventPlace } from './format';
import { AllClearCard, MainCard, TodoCard } from './NextMeetingCards';
import { useCalendarPrefsQuery, useNextMeetingQuery } from './queries';
import { QuickCreatePopover } from './QuickCreatePopover';
import { useQuickMeeting } from './QuickMeetingContext';
import type { NextMeetingAction, PendingAction, WireEvent } from './types';
import { useNow } from './useNow';

/** Once Join is near, the pill rings in its badge's colour. */
const PILL_RING: Partial<Record<ChipKind, string>> = {
  imminent: 'border-primary/40 ring-4 ring-primary/10',
  starting: 'border-primary/40 ring-4 ring-primary/10',
  live: 'border-success/40 ring-4 ring-success/10',
};

const ACTION_TONE: Record<PendingAction, NonNullable<BadgeProps['tone']>> = { mom: 'violet', allocate: 'amber' };

const FACE_KINDS: ReadonlySet<ChipKind> = new Set<ChipKind>(['next', 'soon', 'live']);

const showsFaces = (view: ChipView) => FACE_KINDS.has(view.kind) && view.attendees.length > 0;

const CARD_CLASS = 'w-[360px]';

function Lead({ view }: { view: ChipView }) {
  switch (view.kind) {
    case 'none':
      return (
        <span className="bg-success-light text-success-fg flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
          <CalendarCheck className="h-3.5 w-3.5" aria-hidden />
        </span>
      );
    case 'next':
      return (
        <span className="bg-primary/10 text-primary flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
          <CalendarClock className="h-3.5 w-3.5" aria-hidden />
        </span>
      );
    case 'action':
      return (
        <Badge tone={ACTION_TONE[view.pendingAction ?? 'mom']} className="shrink-0">
          {view.when}
        </Badge>
      );
    case 'soon':
      return (
        <Badge tone="blue" className="shrink-0 tabular-nums">
          {view.when}
        </Badge>
      );
    case 'imminent':
      return (
        <Badge tone="blue" variant="solid" className="shrink-0 tabular-nums">
          {view.when}
        </Badge>
      );
    case 'starting':
      return (
        <Badge tone="blue" variant="solid" className="shrink-0">
          <PulseDot />
          {view.when}
        </Badge>
      );
    case 'live':
      return (
        <Badge tone="green" variant="solid" className="shrink-0 tabular-nums">
          <PulseDot />
          {view.when}
        </Badge>
      );
  }
}

function Title({ view }: { view: ChipView }) {
  if (view.locked) {
    return (
      <span className="text-foreground flex min-w-0 items-center gap-1.5 text-lg font-medium">
        <Lock className="text-muted-foreground h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="truncate">{CHIP_COPY.privateMeeting}</span>
      </span>
    );
  }
  if (view.kind === 'action' && !view.title) return null;
  return (
    <span
      className={cn(
        'text-foreground min-w-0 max-w-28 truncate text-lg xl:max-w-56',
        view.kind === 'next' ? 'font-semibold' : 'font-medium',
      )}
    >
      {view.title ?? CHIP_COPY.meeting}
    </span>
  );
}

/** Below xl the room and faces fold away and the title truncates, so the pill keeps its badge and Join. */
function ChipBody({ view }: { view: ChipView }) {
  if (view.kind === 'none') {
    return <span className="text-muted-foreground whitespace-nowrap text-lg">{CHIP_COPY.free}</span>;
  }
  const room = view.room && view.kind !== 'next' && (
    <span className="text-muted-foreground hidden min-w-0 items-center gap-1 text-lg xl:flex">
      <DoorOpen className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="max-w-40 truncate">{view.room}</span>
    </span>
  );
  return (
    <>
      <Title view={view} />
      {view.kind === 'next' && (
        <>
          <span className="text-muted-foreground shrink-0 text-lg tabular-nums">{view.when}</span>
          {view.relative && (
            <span className="text-primary shrink-0 text-lg font-medium tabular-nums">{view.relative}</span>
          )}
        </>
      )}
      {view.kind === 'action' && view.day && (
        <span className="text-muted-foreground shrink-0 text-lg">{view.day}</span>
      )}
      {room}
      {showsFaces(view) && <Faces people={view.attendees} className="hidden pl-0.5 xl:flex" />}
    </>
  );
}

function PlaceLine({ place, className }: { place: EventPlace; className?: string }) {
  const Icon = place.kind === 'room' ? DoorOpen : MapPin;
  return (
    <p className={cn('flex min-w-0 items-start gap-1.5', className)}>
      <Icon className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="sr-only">{CHIP_COPY[place.kind]}: </span>
      <span className="line-clamp-2 min-w-0 break-words">{place.label}</span>
    </p>
  );
}

/** The live badge's seconds stay out of the label, so a screen reader is not handed a ticking clock. */
function chipAriaLabel(view: ChipView): string {
  if (view.kind === 'none') return CHIP_COPY.free;
  if (view.kind === 'action') return [CHIP_COPY.waitingOnYou, view.when, view.title, view.day].filter(Boolean).join('. ');
  const when =
    view.kind === 'live'
      ? CHIP_COPY.liveNow
      : [view.when, view.relative].filter(Boolean).join(', ');
  return [
    CHIP_COPY.chipLabel,
    view.locked ? CHIP_COPY.privateMeeting : (view.title ?? CHIP_COPY.meeting),
    when,
    view.place ? `${CHIP_COPY[view.place.kind]}: ${view.place.label}` : '',
    view.attendees.length > 0 ? CHIP_COPY.people(view.attendees.length) : '',
  ]
    .filter(Boolean)
    .join('. ');
}

type OpenCard = 'main' | 'todo' | null;

/**
 * The shell-wide next-meeting bar (QUICK-MEETING-PLAN §2.5) on `GET /events/next`, at the far
 * left of the top bar, with Schedule meeting and Book a room as popovers. Meetings waiting on
 * the caller open from the card. The server decides what matters; the client formats on a tick.
 */
export function NextMeetingChip() {
  const qm = useQuickMeeting();
  const enabled = !!qm?.access.canViewMeetings;
  const userId = qm?.access.userId ?? null;
  const slug = qm?.access.slug ?? null;
  const next = useNextMeetingQuery(userId, slug, enabled);
  const prefs = useCalendarPrefsQuery(slug, enabled);
  const current = next.data ? (next.data.live ?? next.data.next) : null;
  // Live counts mm:ss, and inside the soon window Join must appear on the minute, not a coarse tick late.
  // The coarse clock decides when to switch, one tick early so the fine clock is already running.
  const coarse = useNow(QM_POLL.CHIP_TICK_MS, enabled);
  const close =
    current != null &&
    (isLive(current) || Date.parse(current.start) - coarse <= CHIP.SOON_MS + QM_POLL.CHIP_TICK_MS);
  const fine = useNow(QM_POLL.PILL_TICK_MS, enabled && close);
  const now = close ? Math.max(fine, coarse) : coarse;
  const [open, setOpen] = useState<OpenCard>(null);
  const [layerOpen, setLayerOpen] = useState(false);
  if (!qm || !enabled || !next.data) return null;

  const data = next.data;
  const h24 = prefs.data?.h24 ?? false;
  const hideTitles = prefs.data?.hideTitles ?? false;
  const view = chipView(data, now, h24, hideTitles);
  const { focus } = view;
  const later = (data.later_today ?? []).slice(0, CHIP.LIST_LIMIT);

  const { canScheduleMeetings, canBookRooms } = qm.access;
  const quickCreate = { slug: slug ?? '', selfId: userId, h24, openCalendarPath: qm.openCalendarPath };
  const go = (path: string) => {
    setOpen(null);
    qm.openCalendarPath(path);
  };
  const openEvent = (e: WireEvent) => go(CALENDAR_PATH.event(e.id, e.start));
  const openAction = (a: NextMeetingAction) =>
    go(a.recording_id ? CALENDAR_PATH.recording(a.recording_id) : CALENDAR_PATH.event(a.event.id, a.event.start));
  const openCalendar = () => go(CALENDAR_PATH.HOME);

  // The title and room truncate in the pill, so the tooltip carries them whole (times only while titles are hidden).
  const tooltip =
    focus &&
    [
      hideTitles ? null : eventLabel(focus),
      formatDayRange(focus, h24),
      view.attendees.length > 0 ? CHIP_COPY.people(view.attendees.length) : null,
    ]
      .filter(Boolean)
      .join(' · ');

  const todoCard = (
    <TodoCard
      count={view.count}
      actions={view.actions}
      h24={h24}
      onOpenAction={openAction}
      onOpenCalendar={openCalendar}
    />
  );
  const card =
    view.kind === 'action' ? (
      todoCard
    ) : focus ? (
      <MainCard
        view={view}
        focus={focus}
        now={now}
        h24={h24}
        later={later}
        onOpenEvent={openEvent}
        onOpenTodo={() => setOpen('todo')}
        onLayerChange={setLayerOpen}
      />
    ) : (
      <AllClearCard onOpenCalendar={openCalendar} />
    );

  return (
    <TooltipProvider delayDuration={400}>
      <div
        className={cn(
          'border-border bg-background flex h-8 min-w-0 items-center rounded-full border shadow-sm transition-[border-color,box-shadow] duration-300 motion-reduce:transition-none',
          PILL_RING[view.kind],
        )}
      >
        {/* The to-do list swaps into the same card, so it needs no pill in the bar. */}
        <Popover open={open !== null} onOpenChange={(isOpen) => setOpen(isOpen ? 'main' : null)}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label={chipAriaLabel(view)}
                  className={cn(
                    'hover:bg-muted/50 focus-visible:ring-ring flex h-full min-w-0 items-center gap-2 rounded-full pl-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset',
                    view.joinUrl ? 'pr-2' : showsFaces(view) ? 'pr-3 xl:pr-1' : 'pr-3',
                  )}
                >
                  <Lead view={view} />
                  <ChipBody view={view} />
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            {tooltip && open === null && (
              <TooltipContent side="bottom" className="break-words">
                <p>{tooltip}</p>
                {view.place && <PlaceLine place={view.place} className="mt-0.5" />}
              </TooltipContent>
            )}
          </Tooltip>
          {view.joinUrl && (
            <Button asChild size="xs" className="mr-1 shrink-0 rounded-full px-2.5">
              <a
                href={view.joinUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${CHIP_COPY.join}: ${focus ? eventLabel(focus) : ''}`}
              >
                <Video className="hidden xl:block" aria-hidden />
                {CHIP_COPY.join}
              </a>
            </Button>
          )}
          <PopoverContentWithArrow
            side="bottom"
            align="start"
            collisionPadding={8}
            className={CARD_CLASS}
            // The Extend menu and End dialog portal out of the card; while one is up, the click
            // or focus that dismisses it must not also close the card.
            onInteractOutside={(e) => {
              if (layerOpen) e.preventDefault();
            }}
          >
            {open === 'todo' ? todoCard : card}
          </PopoverContentWithArrow>
        </Popover>
        {(canScheduleMeetings || canBookRooms) && (
          <>
            <span className="bg-border h-4 w-px shrink-0" aria-hidden />
            <div className="flex shrink-0 items-center gap-0.5 px-1">
              {canScheduleMeetings && (
                <QuickCreatePopover mode="meeting" icon={CalendarPlus} {...quickCreate} />
              )}
              {canBookRooms && <QuickCreatePopover mode="room" icon={DoorOpen} {...quickCreate} />}
            </div>
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
